import React from 'react';
import { LogOut } from 'lucide-react';
import { formatPeopleLabel } from '../../utils/reservationPeople.ts';
import type { SalaOcupacao } from '../../services/api.ts';

type FloorStats = {
  total: number;
  occupied: number;
  available: number;
};

type RoomCapacityOverviewProps = {
  currentFloorData: any;
  currentFloorStats: FloorStats;
  currentFloorSalaOcupacoes: SalaOcupacao[];
  reservationById: Map<string, any>;
  openSalaReleaseModal: (ocupacao: SalaOcupacao) => void;
  onConfigureCapacity: () => void;
};

const RoomCapacityOverview: React.FC<RoomCapacityOverviewProps> = ({
  currentFloorData,
  currentFloorStats,
  currentFloorSalaOcupacoes,
  reservationById,
  openSalaReleaseModal,
  onConfigureCapacity,
}) => (
  <div className="room-capacity-overview">
    <div className="room-capacity-heading">
      <div>
        <span className="room-capacity-eyebrow">Ocupação por capacidade</span>
        <h3>{currentFloorData.name}</h3>
        <p>
          Cada grupo sentado desconta cadeiras da capacidade deste ambiente.
        </p>
      </div>
      <button
        type="button"
        className="room-capacity-settings-link"
        onClick={onConfigureCapacity}
      >
        Configurar capacidade
      </button>
    </div>

    <div className="room-capacity-metrics">
      <div className="room-capacity-metric is-total">
        <span>Capacidade</span>
        <strong>{currentFloorStats.total}</strong>
        <small>cadeiras cadastradas</small>
      </div>
      <div className="room-capacity-metric is-occupied">
        <span>Ocupadas</span>
        <strong>{currentFloorStats.occupied}</strong>
        <small>com clientes sentados</small>
      </div>
      <div className="room-capacity-metric is-available">
        <span>Disponíveis</span>
        <strong>{currentFloorStats.available}</strong>
        <small>para novos clientes</small>
      </div>
    </div>

    {currentFloorStats.total <= 0 && (
      <div className="room-capacity-notice" role="alert">
        <strong>Informe a capacidade deste ambiente.</strong>
        <span>
          Sem uma capacidade maior que zero, não será possível sentar reservas ou clientes da fila.
        </span>
      </div>
    )}

    <div className="room-capacity-groups">
      <div className="room-capacity-groups-header">
        <div>
          <h4>Grupos sentados</h4>
          <p>Libere todas as pessoas ou apenas quem já saiu.</p>
        </div>
        <span className="room-capacity-groups-count">
          {currentFloorSalaOcupacoes.length} grupo{currentFloorSalaOcupacoes.length !== 1 ? 's' : ''}
        </span>
      </div>

      {currentFloorSalaOcupacoes.length === 0 && (
        <div className="room-capacity-empty">
          Nenhum grupo está sentado neste ambiente.
        </div>
      )}

      {currentFloorSalaOcupacoes.length > 0 && (
        <ul className="room-capacity-group-list">
          {currentFloorSalaOcupacoes.map((ocupacao) => {
            const linkedReservation = ocupacao.reserva_id
              ? reservationById.get(String(ocupacao.reserva_id))
              : null;
            const groupName = ocupacao.nome_cliente
              || linkedReservation?.name
              || (ocupacao.waitlist_id ? 'Cliente da fila' : 'Grupo sem nome');
            const presentCount = Math.max(
              0,
              Number(ocupacao.numero_pessoas_presentes) || 0
            );
            const initialCount = Math.max(
              presentCount,
              Number(ocupacao.numero_pessoas_inicial) || presentCount
            );
            const seatedTime = ocupacao.seated_at
              ? new Date(ocupacao.seated_at).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })
              : null;

            return (
              <li key={String(ocupacao.id)} className="room-capacity-group-item">
                <div className="room-capacity-group-main">
                  <div className="room-capacity-group-name-row">
                    <strong>{groupName}</strong>
                    <span>
                      {ocupacao.waitlist_id ? 'Fila de espera' : 'Reserva'}
                    </span>
                  </div>
                  <div className="room-capacity-group-meta">
                    <span>{formatPeopleLabel(presentCount)} no ambiente</span>
                    {initialCount !== presentCount && (
                      <span>Grupo inicial: {formatPeopleLabel(initialCount)}</span>
                    )}
                    {seatedTime && <span>Sentou às {seatedTime}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  className="room-today-action is-danger"
                  onClick={() => openSalaReleaseModal(ocupacao)}
                >
                  <LogOut size={15} />
                  Levantar
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  </div>
);

export default RoomCapacityOverview;
